<template>
    <ContentComp
        ref="content"
        class="h-100"
        :load-state="LoadState"
    >
        <ToolbarComp>
            <template #first
                ><ButtonComp
                    button-type="Delete"
                    @click="onDeleteClick"
                    :disabled="selectedCount === 0"
                ></ButtonComp
            ></template>
            <template #middle>
                <InputTypeAheadContestant
                    class="w-100"
                    @item-selected="onItemSelected"
                />
            </template>
            <template #last><ButtonComp button-type="Refresh"></ButtonComp></template>
        </ToolbarComp>

        <GridComp
            ref="grid"
            :gridSetup="gridSetup"
            class="overflow-auto h-100"
            resize-strategy="sizeColumnsToFit"
            @onSelectionChanged="
                (items: ContestantFavorite[]) => {
                    selectedCount = items.length;
                }
            "
        />
    </ContentComp>
</template>
<script setup lang="ts">
import { ButtonComp, GridButton, GridComp, ToolbarComp } from '@/components';
import { GridColumnDefinition, GridDataSource, GridSetup, type IGridCompExposed } from '@/components/Grid/Classes';
import { GridColumnOptionsBuilder } from '@/components/Grid/Classes/GridColumnOptions';
import type { IGridRowGetterParams } from '@/components/Grid/Classes/GridDataSource';
import ContentComp from '@/components/layout/ContentComp.vue';
import InputTypeAheadContestant from '@/components/Typeahead/General/InputTypeAheadContestant.vue';
import { useTaskLoader } from '@/composeables';
import { useDialogBuilder } from '@/composeables/dialog/dialogbuilder';
import { BaseObjDao, RaceContestantDetailsDao } from '@/dao';
import { $ti, $tm } from '@/locize';
import { ContestantFavorite, RaceContestantDetails, StringArray, TypeAheadItem } from '@/objects/generated';
import { useContestantStore, useEventStoreRef, useToastStore } from '@/stores';
import { propertyof } from '@/utilities';
import { useElementVisibility } from '@vueuse/core';
import { computed } from 'vue';
import { watch } from 'vue';
import { ref } from 'vue';
import ContestantFavoriteDetails from '../Contestants/ContestantFavoriteDetails.vue';

// The grid comp
const grid = ref<IGridCompExposed<ContestantFavorite> | null>(null);

/**
 * Contains the dataset
 */
const data = ref<ContestantFavorite[]>([]);

/**
 * HTML element ref for the content component, we will use this to check if the component is visible so we can load the data when it becomes visible
 */
const content = ref<HTMLElement | null>(null);

/**
 * The current event
 */
const { eventID } = useEventStoreRef();

// Contestant store
const contestantStore = useContestantStore();

/**
 * Set up event listener for when the visibility of the component changes, so we can load the data when it becomes visible
 */
const targetIsVisible = useElementVisibility(content);

const { AddLoader, LoadState } = useTaskLoader();

/**
 * The number of selected rows in the grid, we will use this to enable/disable the delete button
 */
const selectedCount = ref<number>(0);

const contestantSet = computed(() => {
    const set = new Set<number>();
    data.value.forEach((fav) => set.add(fav.ContestantID));
    return set;
});

/**
 * Set up the datasource
 */
const dataSource = new GridDataSource<ContestantFavorite>({
    totalNumberOfRows: () => data.value.length, // Maybe this needs to change for a server side data source

    rowGetter: (params: IGridRowGetterParams<ContestantFavorite>) => {
        const slice = data.value.slice(params.startRow, params.endRow);

        params.successCallback(slice, data.value, data.value.length);
    },
});

/**
 * Columns
 */
const contestantColumn = new GridColumnOptionsBuilder<ContestantFavorite>(propertyof<ContestantFavorite>('ContestantID'), 'contestant.name')
    .ValueGetter((params) => {
        const contestant = contestantStore.GetContestantById(params.ContestantID);
        if (contestant) {
            return contestantStore.GetDisplayNameForContestantObject(contestant);
        }
        return '';
    })
    .Build();

const gridColumnDefinitions = [
    contestantColumn,
    new GridColumnOptionsBuilder<ContestantFavorite>(propertyof<ContestantFavorite>('Comment'), 'general.description')
        .Size(200, 900)
        .ReadOnly()
        .CellRenderer(GridButton, {
            icon: 'fa-pencil',
            text: 'general.edit',
            moretext: (row: ContestantFavorite) => (row.Comment ? `: ${row.Comment}` : ''),
            onClick: (row: ContestantFavorite) => onCommentClick(row),
        })
        .Build(),
];

/**
 * Set up the grid
 */
const gridSetup = new GridSetup<ContestantFavorite>({
    rowIdGetter: (row: ContestantFavorite) => row.ID.toString(),
    datasource: dataSource,
    GridColumnDefinition: new GridColumnDefinition({
        ColumnOptions: gridColumnDefinitions,
    }),
    rowSelection: 'multiple',
    hasCheckboxSelectionColumn: true,
    hideStatusbar: false,
    gridkey: 'contestantfavoritegrid',
});

/**
 * Whenever the edit button is clicked, we want to open a popup with the comment and allow the user to edit it. Once the user is done, we want to save the comment and update the grid
 * @param {ContestantFavorite} row
 */
const onCommentClick = async (row: ContestantFavorite) => {
    const contestant = contestantStore.GetContestantById(row.ContestantID);
    if (!contestant) {
        useToastStore().addError($tm('contestantfavorite.loadcontestantfailed'));
        return;
    }

    // We need to load the ContestantDetails for the contestant
    const contestantDetails = ref<RaceContestantDetails | null>(
        ((await BaseObjDao.Load(RaceContestantDetails, eventID.value, [contestant.RaceContestants.at(0)!.ID])) ?? []).find(
            (details) => details.ContestantID === row.ContestantID,
        ) ?? null,
    );

    console.log('HELP', contestantDetails);

    if (!contestantDetails.value) {
        useToastStore().addError($tm('contestantfavorite.loaddetailsfailed'));
        return;
    }

    useDialogBuilder()
        .withTitle(contestantStore.GetDisplayNameForContestantObject(contestant, 'fullwithbib'))
        .withBodyComponent(ContestantFavoriteDetails, {
            vmodel: contestantDetails,
        })
        .withCancelButton({
            action: () => {},
        })
        .withSaveButton({
            action: async () => {
                if (contestantDetails.value) {
                    let memoArray = StringArray.FromJsValues({ Values: [contestantDetails.value?.Memo ?? ''] });
                    try {
                        const resp = await RaceContestantDetailsDao.SaveContestantMemo(
                            eventID.value,
                            contestantDetails.value.ContestantID,
                            memoArray,
                        );
                    } catch (error) {
                        useToastStore().addError($tm('contestantfavorite.savedetailsfailed'));
                        return false;
                    }

                    data.value = await BaseObjDao.Load(ContestantFavorite, eventID.value);
                    grid.value!.refreshCachedData();
                }
                return true;
            },
        })
        .withOptions({
            Size: 'lg',
        })
        .Open();
};

/**
 * Whenever we select a item from the typeahead, we want to add a new item to the grid with the selected contestant and then open the comment dialog for that item
 * @param {TypeAheadItem} item
 */
const onItemSelected = async (item: TypeAheadItem) => {
    // TODO: We need to create a ContestantFavorite object and save it to the backend, and then reload the data to get the new item in the grid. For now, we will just add it to the grid directly

    // We check if the item allready exists in the grid, if it does we will not add it again
    if (contestantSet.value.has(item.ID)) {
        const contestant = contestantStore.GetContestantById(item.ID);
        useToastStore().addInfo(
            $ti(
                'contestantfavorite.alreadyexists',
                { name: contestantStore.GetDisplayNameForContestantObject(contestant!, 'fullwithbib') },
                false,
            ),
        );
        return;
    }

    const newFavorite = ContestantFavorite.FromJsValues({
        EntityState: 'Added',
        ContestantID: item.ID,
        EventID: eventID.value,
        Comment: '',
    });

    await BaseObjDao.Save(ContestantFavorite, eventID.value, [newFavorite]);

    // Reload the data to get the new item in the grid
    data.value = await BaseObjDao.Load(ContestantFavorite, eventID.value);
    grid.value!.refreshCachedData();
};

/**
 * Load all the data from the backend and refresh the grid.
 */
const loadData = async () => {
    AddLoader(async () => {
        // Fetch the data
        data.value = await BaseObjDao.Load(ContestantFavorite, eventID.value);

        grid.value?.purgeCachedData();
    }, '');
};

const onDeleteClick = async () => {
    if (!grid.value) return;
    const selectedRows = grid.value!.getSelectedRows();

    if (selectedRows.length === 0) {
        // No rows selected, show a message or simply return
        return;
    }

    useDialogBuilder()
        .withTitle('general.deletedselectedmsg')
        .withBodyText('general.deletedselectedmsg', { count: selectedRows.length })
        .withCancelButton({
            action: () => {},
        })
        .withOkButton({
            action: async () => {
                if (selectedRows.length > 0) {
                    selectedRows.forEach((row) => {
                        row.Delete();
                    });

                    await BaseObjDao.Save(ContestantFavorite, eventID.value, selectedRows);

                    data.value = await BaseObjDao.Load(ContestantFavorite, eventID.value);
                    grid.value!.refreshCachedData();
                }
            },
        })
        .Open();
};

watch(targetIsVisible, async () => {
    if (!content.value) return;

    if (targetIsVisible.value) {
        loadData();
    }
});
</script>
